const componentModules = [
  ['product-sku-component', '@theme/product-sku'],
  ['product-roomvo-sku-component', '@theme/product-roomvo-sku'],
  ['product-specifications', '@theme/product-specifications'],
  ['product-inventory', '@theme/product-inventory'],
  ['media-gallery', '@theme/media-gallery'],
  ['volume-pricing', '@theme/volume-pricing'],
  ['price-per-item', '@theme/price-per-item'],
  ['volume-pricing-info', '@theme/volume-pricing-info'],
  ['quick-add-component, quick-add-dialog', '@theme/quick-add'],
  ['variant-picker', '@theme/variant-picker'],
  ['product-card, product-card-link, swatches-variant-picker-component', '@theme/product-card'],
  ['product-form-component, add-to-cart-component', '@theme/product-form'],
  ['product-buy-form-stock', '@theme/product-buy-form-stock'],
  ['product-buy-form-backorder', '@theme/product-buy-form-backorder'],
  ['accordion-custom', '@theme/accordion-custom'],
  ['deferred-media, product-model', '@theme/media'],
  ['product-price', '@theme/product-price'],
  ['product-title', '@theme/product-title'],
  ['show-more-component', '@theme/show-more'],
  ['slideshow-component', '@theme/slideshow'],
  ['layered-slideshow-component', '@theme/layered-slideshow'],
  ['anchored-popover-component', '@theme/anchored-popover'],
  ['floating-panel-component', '@theme/floating-panel'],
  ['video-background-component', '@theme/video-background'],
  ['quantity-selector-component', '@theme/component-quantity-selector'],
  ['rte-formatter', '@theme/rte-formatter'],
];

const loadedComponentModules = new Set();

/**
 * Loads modules for custom elements that exist in the supplied DOM subtree.
 * @param {Document | Element} [root]
 */
function loadPresentComponentModules(root = document) {
  for (const [selector, moduleName] of componentModules) {
    if (loadedComponentModules.has(moduleName)) continue;

    const rootMatches = root instanceof Element && root.matches(selector);
    if (!rootMatches && !root.querySelector(selector)) continue;

    loadedComponentModules.add(moduleName);
    import(moduleName).catch((error) => {
      loadedComponentModules.delete(moduleName);
      console.error(`Unable to load ${moduleName}`, error);
    });
  }
}

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  const groutTrigger = event.target.closest('[data-grout-calculator-trigger]');
  if (groutTrigger) {
    const [selector, method] = (groutTrigger.getAttribute('on:click') ?? '').split('/');
    if (!selector.startsWith('#') || method !== 'openGroutCalculator') return;

    const calculatorId = selector.slice(1);
    const calculator = document.getElementById(calculatorId);
    if (!calculator || typeof calculator.openGroutCalculator === 'function') return;

    try {
      await import('@theme/product-grout-calculator');
      await customElements.whenDefined('product-grout-calculator');
      document.getElementById(calculatorId)?.openGroutCalculator?.();
    } catch (error) {
      console.error('Unable to load @theme/product-grout-calculator', error);
    }

    return;
  }

  const zoomTrigger = event.target.closest('[on\\:click*="zoom-dialog"]');
  if (!zoomTrigger) return;

  const value = zoomTrigger.getAttribute('on:click') ?? '';
  const parts = value.split('/');
  if (parts.length < 3 || !parts[0].startsWith('#') || parts[1] !== 'open') return;

  const dialog = document.querySelector(parts[0]);
  if (!dialog || typeof dialog.open === 'function') return;

  try {
    await import('@theme/zoom-dialog');
    await customElements.whenDefined('zoom-dialog');
    const index = Number(parts[2]);
    dialog.open?.(index, event);
  } catch (error) {
    console.error('Unable to load @theme/zoom-dialog', error);
  }
});

/**
 * Restores the real sources of an image or picture marked for deferred loading.
 * @param {Element} root
 */
function hydrateDeferredImage(root) {
  const sources = root.matches('[data-deferred-srcset], [data-deferred-src]')
    ? [root]
    : root.querySelectorAll('[data-deferred-srcset], [data-deferred-src]');

  for (const source of sources) {
    if (source instanceof HTMLSourceElement && source.dataset.deferredSrcset) {
      source.srcset = source.dataset.deferredSrcset;
      delete source.dataset.deferredSrcset;
    }

    if (source instanceof HTMLImageElement) {
      if (source.dataset.deferredSrcset) {
        source.srcset = source.dataset.deferredSrcset;
        delete source.dataset.deferredSrcset;
      }

      if (source.dataset.deferredSrc) {
        source.src = source.dataset.deferredSrc;
        delete source.dataset.deferredSrc;
      }
    }
  }

  root.removeAttribute('data-deferred-image');
}

const deferredImageObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            deferredImageObserver?.unobserve(entry.target);
            hydrateDeferredImage(entry.target);
          }
        },
        { rootMargin: '0px' }
      )
    : null;

/**
 * Watches deferred images in the supplied DOM subtree.
 * @param {Document | Element} [root]
 */
function observeDeferredImages(root = document) {
  const images = root instanceof Element && root.matches('[data-deferred-image]')
    ? [root]
    : root.querySelectorAll('[data-deferred-image]');

  for (const image of images) {
    if (deferredImageObserver) deferredImageObserver.observe(image);
    else hydrateDeferredImage(image);
  }
}

function hydrateImagesNearViewport() {
  for (const image of document.querySelectorAll('[data-deferred-image]')) {
    const rect = image.getBoundingClientRect();
    const isNearViewport =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > -400 &&
      rect.top < window.innerHeight + 400 &&
      rect.right > -400 &&
      rect.left < window.innerWidth + 400;

    if (!isNearViewport) continue;

    deferredImageObserver?.unobserve(image);
    hydrateDeferredImage(image);
  }
}

/**
 * Shopify injects account.js when `<shopify-account>` is in the live DOM.
 * Keep signed-out instances in `<template>` until hover/focus/click so Lighthouse TBT is not spent on it.
 * @param {Element} root
 * @param {boolean} [openAfterMount]
 */
async function mountDeferredShopifyAccount(root, openAfterMount = false) {
  if (!(root instanceof HTMLElement) || root.dataset.shopifyAccountMounted === 'true') return;

  const template = root.querySelector(':scope > template');
  const trigger = root.querySelector('[data-shopify-account-trigger]');
  if (!(template instanceof HTMLTemplateElement)) return;

  root.dataset.shopifyAccountMounted = 'true';
  const content = template.content.cloneNode(true);
  template.remove();
  trigger?.remove();
  root.append(content);

  if (!openAfterMount) return;

  try {
    await Promise.race([
      customElements.whenDefined('shopify-account'),
      new Promise((resolve) => {
        setTimeout(resolve, 2500);
      }),
    ]);
    const account = root.querySelector('shopify-account');
    const button = account?.querySelector('button');
    if (button instanceof HTMLElement) button.click();
  } catch (error) {
    console.error('Unable to open deferred shopify-account', error);
  }
}

function initializeDeferredShopifyAccount() {
  const onIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const root = event.target.closest('[data-shopify-account-defer]');
    if (!root || root.dataset.shopifyAccountMounted === 'true') return;

    const isClick = event.type === 'click';
    if (isClick) event.preventDefault();
    mountDeferredShopifyAccount(root, isClick);
  };

  document.addEventListener('pointerenter', onIntent, true);
  document.addEventListener('focusin', onIntent);
  document.addEventListener('click', onIntent, true);
}

function initializeDomLoading() {
  loadPresentComponentModules();
  observeDeferredImages();

  const domObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;

        loadPresentComponentModules(node);
        observeDeferredImages(node);
      }
    }
  });

  domObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', hydrateImagesNearViewport, { once: true });
}

function initializeRoomvo() {
  const roomvoUrl = 'https://cdn.roomvo.com/static/scripts/b2b/capietra.js';
  /** Late fallback so compatible stimr buttons can still appear for passive readers. */
  const IDLE_LOAD_MS = 10000;
  /** @type {Promise<void> | null} */
  let roomvoLoadPromise = null;

  /**
   * Stimr buttons start `visibility: hidden` in Liquid. Roomvo validates the SKU and
   * shows + wires `onclick` itself. Load the script on page intent (not first paint)
   * so incompatible products never show a dead button, without paying TBT on boot.
   */
  const loadRoomvo = () => {
    if (roomvoLoadPromise) return roomvoLoadPromise;

    roomvoLoadPromise = new Promise((resolve) => {
      const existing = document.querySelector(`script[src="${roomvoUrl}"]`);
      if (existing) {
        if (window.roomvo) {
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => resolve(), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = roomvoUrl;
      script.async = true;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(script);
    });

    return roomvoLoadPromise;
  };

  // Footer "Tile visualizer" / Roomvo Direct start — load immediately and autolaunch.
  if (new URLSearchParams(location.search).get('roomvoStartVisualizer') === 'true') {
    loadRoomvo();
    return;
  }

  if (!document.querySelector('.roomvo-container, .roomvo-direct-container')) return;

  /** @type {Array<[EventTarget, string, AddEventListenerOptions | boolean | undefined]>} */
  const intentListeners = [];
  let idleTimer = 0;

  const teardownIntentListeners = () => {
    for (const [target, type, options] of intentListeners) {
      target.removeEventListener(type, onIntent, options);
    }
    intentListeners.length = 0;
    window.clearTimeout(idleTimer);
  };

  const onIntent = () => {
    teardownIntentListeners();
    loadRoomvo();
  };

  /**
   * @param {EventTarget} target
   * @param {string} type
   * @param {AddEventListenerOptions} [options]
   */
  const addIntentListener = (target, type, options) => {
    target.addEventListener(type, onIntent, options);
    intentListeners.push([target, type, options]);
  };

  if (window.matchMedia('(pointer: coarse)').matches) {
    addIntentListener(document, 'pointerdown', { passive: true });
    addIntentListener(document, 'touchstart', { passive: true });
    addIntentListener(window, 'scroll', { passive: true });
  } else {
    addIntentListener(document, 'pointerdown', { passive: true });
    addIntentListener(document, 'pointermove', { passive: true });
    addIntentListener(document, 'keydown');
  }

  idleTimer = window.setTimeout(onIntent, IDLE_LOAD_MS);
}

function initializeDeferredPdpModules() {
  let quoteFormRequested = false;

  const loadQuoteForm = () => {
    if (quoteFormRequested) return;

    quoteFormRequested = true;
    import('@theme/product-quote-form').catch((error) => {
      console.error('Unable to load @theme/product-quote-form', error);
    });
  };

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('[data-hubspot-form-trigger]')) return;
      loadQuoteForm();
    },
    { once: false, passive: true }
  );

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-hubspot-form-trigger]')) return;
    loadQuoteForm();
  });

  let stickyAddToCartRequested = false;

  const loadStickyAddToCart = () => {
    if (stickyAddToCartRequested) return;
    if (!document.querySelector('sticky-add-to-cart')) return;

    stickyAddToCartRequested = true;
    import('@theme/sticky-add-to-cart').catch((error) => {
      stickyAddToCartRequested = false;
      console.error('Unable to load @theme/sticky-add-to-cart', error);
    });
  };

  window.addEventListener('scroll', loadStickyAddToCart, { once: true, passive: true });

  if (document.querySelector('sticky-add-to-cart')) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadStickyAddToCart, { timeout: 4000 });
    } else {
      window.setTimeout(loadStickyAddToCart, 4000);
    }
  }
}

initializeDomLoading();
initializeDeferredShopifyAccount();
initializeDeferredPdpModules();
initializeRoomvo();
